import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Box,
} from "@mui/material";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { SoftButton, useDialogPaperSx } from "../../hatim/soft/SoftKit";
import { useAddParticipationTemplateMutation } from "../../../api/hatimManagementApi";

export default function AddParticipationCodeModal({ open, hatimTemplateId, onClose, onCreated }) {
  const { t } = useScriptMode();
  const paperSx = useDialogPaperSx();
  const [maxCount, setMaxCount] = useState("");
  const [generated, setGenerated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [addCode, { isLoading }] = useAddParticipationTemplateMutation();

  useEffect(() => {
    if (!open) {
      setMaxCount("");
      setGenerated(null);
      setCopied(false);
      setError("");
    }
  }, [open]);

  const num = Number(maxCount);
  const canSubmit =
    String(maxCount).trim() !== "" &&
    Number.isFinite(num) &&
    num >= 1 &&
    !isLoading &&
    !generated;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      const res = await addCode({ hatimTemplateId, maxCount: num }).unwrap();
      const code = res?.data?.participationTemplate?.code;
      setGenerated(code || "");
      onCreated && onCreated();
    } catch (err) {
      const msg = err?.message || err?.data?.message || t("hatim_mgmt_action_error");
      setError(msg);
    }
  };

  const handleCopy = async () => {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(generated);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // ignore — manual copy still possible
    }
  };

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: paperSx }}>
      <DialogTitle sx={{ fontWeight: 500 }}>
        {t("hatim_mgmt_add_participation_modal_title")}
      </DialogTitle>
      <DialogContent>
        {generated ? (
          <Box display="flex" flexDirection="column" gap={1.5} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {t("hatim_mgmt_add_participation_result_label")}
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontFamily: "monospace", letterSpacing: 2, fontWeight: 700 }}
            >
              {generated}
            </Typography>
            <SoftButton tone="soft" onClick={handleCopy}>
              {copied
                ? t("hatim_mgmt_add_participation_copied")
                : t("hatim_mgmt_add_participation_copy")}
            </SoftButton>
          </Box>
        ) : (
          <>
            <TextField
              autoFocus
              type="number"
              fullWidth
              label={t("hatim_mgmt_add_participation_max_count")}
              value={maxCount}
              onChange={(e) => setMaxCount(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              margin="normal"
            />
            {error ? (
              <Typography color="error" variant="body2" mt={1}>{error}</Typography>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {generated ? (
          <SoftButton tone="soft" onClick={onClose}>{t("hatim_mgmt_add_participation_close")}</SoftButton>
        ) : (
          <>
            <SoftButton tone="cancel" onClick={onClose}>{t("hatim_mgmt_create_cancel")}</SoftButton>
            <SoftButton tone="primary" onClick={handleSubmit} disabled={!canSubmit}>
              {t("hatim_mgmt_add_participation_submit")}
            </SoftButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
